'use client'

import React, { useState } from 'react'
import { Card } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { ArrowLeft, SquaresFour } from 'phosphor-react'
import { Link } from '@/lib'
import Components from '../components'

export default function CreativeDesign() {

  const designs: Array<{ title: string; image: string; description: string; year?: string }> = [
    {
      title: 'Midway Cup Design',
      image: '/assets/creative-projects/Midway-Cup-Design.PNG',
      description: 'Glassware design featuring a compass-style graphic with train, bridge, and cityscape icons representing the Midway area',
      year: '2020'
    },
    {
      title: 'A Midsummer Night\'s Delirium',
      image: '/assets/creative-projects/Midsummer-Nights-Delirium_A3.png',
      description: 'Promotional poster for a circus event featuring fire performers, aerialists, and theatrical imagery with Art Nouveau-inspired framing',
      year: '2020'
    },
    {
      title: 'Writhing: An Anthology - Cover Design',
      image: '/assets/creative-projects/Front Cover - Final (ebook).png',
      description: 'Book cover design for Wordplay Writer\'s group anthology. Learn more about Wordplay: @wordplayopenmic',
      year: '2020'
    }
  ]

  return (
    <Components.PageContainer>
      <Components.SkipLink />
      <Components.GlassNavigation />
      
      <main id="main-content" tabIndex={-1} className="pt-16 md:pt-32 pb-16 px-4 md:px-6" role="main" aria-label="Design">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <header className="mb-8 md:mb-12">
            <Button
              asChild
              variant="ghost"
              className="mb-6 text-[var(--brutal-ink)] hover:bg-[var(--brutal-panel)]"
            >
              <Link to="/CreativeProjects">
                <ArrowLeft weight="duotone" className="w-4 h-4 mr-2" />
                Back to Creative Projects
              </Link>
            </Button>
            
            <div className="flex items-center gap-4 mb-4">
              <div className="w-12 h-12 bg-gradient-to-br from-amber-500/20 to-yellow-500/20 rounded-full flex items-center justify-center">
                <SquaresFour weight="duotone" className="w-6 h-6 text-[var(--brutal-ink)]" />
              </div>
              <div>
                <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-[var(--brutal-ink)] leading-tight break-words">
                  Design
                </h1>
                <p className="text-base md:text-lg text-[var(--brutal-muted)] mt-2 break-words">
                  Personal design projects, experimental typography, and visual communication work exploring form, function, and meaning
                </p>
              </div>
            </div>
          </header>

          {/* Gallery Grid */}
          {designs.length > 0 ? (
            <section className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6" aria-label="Design gallery">
              {designs.map((design, index) => (
                <Card
                  key={index}
                  className="group relative overflow-hidden brutal-panel hover:bg-[var(--brutal-kraft)] transition-all duration-300 hover:scale-[1.02] hover:shadow-2xl !p-0 !gap-0"
                >
                  <Components.ImageModal
                    src={design.image}
                    alt={design.title}
                  >
                    <div className="relative aspect-[4/3] overflow-hidden border-b-2 border-[var(--brutal-ink)] cursor-pointer">
                      <img
                        src={design.image}
                        alt={design.title}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                    </div>
                  </Components.ImageModal>
                  <div className="p-4">
                    <h3 className="text-lg font-bold text-[var(--brutal-ink)] mb-1 group-hover:text-[var(--brutal-ink)] transition-colors duration-200 break-words">
                      {design.title}
                    </h3>
                    {design.year && (
                      <p className="text-[var(--brutal-muted)] text-xs mb-2">{design.year}</p>
                    )}
                    <p className="text-[var(--brutal-muted)] text-sm leading-relaxed break-words">
                      {design.title === 'Writhing: An Anthology - Cover Design' ? (
                        <>
                          Book cover design for Wordplay Writer's group anthology. Learn more about Wordplay:{' '}
                          <a 
                            href="https://www.instagram.com/wordplayopenmic" 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="text-[var(--brutal-ink)] hover:text-[var(--brutal-muted)] underline transition-colors"
                          >
                            @wordplayopenmic
                          </a>
                        </>
                      ) : (
                        design.description
                      )}
                    </p>
                  </div>
                </Card>
              ))}
            </section>
          ) : (
            <Card className="brutal-panel p-12 text-center">
              <p className="text-[var(--brutal-muted)] text-lg">Design projects coming soon...</p>
            </Card>
          )}
        </div>
      </main>

    </Components.PageContainer>
  )
}
